import React, { useEffect, useState } from 'react'
import './ThemeSettingsTool.scss';
import { useDispatch, useSelector } from 'react-redux';
import { Settings, AdminSettingsErrors, AdminSettingsLoading } from '../../../../store/Settings/Selector';
import { getAdminSettings, updateAdminSettings } from '../../../../store/Settings/Actions';
import { useAuth } from '../../../../services/auth-context';
import { Button } from '@material-ui/core';
import { Loader } from '../../../common/loader/Loader';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export interface ISettingsToolProps {
    getLangString: (value: string) => string;
}

export const SettingsTool = (props: ISettingsToolProps): JSX.Element => {
    const [emailInput, setEmailInput] = useState<string>("");
    const [emails, setEmails] = useState<string[]>([]);
    const [activityDays, setActivityDays] = useState<number>(14);

    const dispatch = useDispatch();
    const { getApiToken } = useAuth();

    const settings = useSelector(Settings);
    const loading = useSelector(AdminSettingsLoading);
    const error = useSelector(AdminSettingsErrors);

    useEffect(() => {
        dispatch(getAdminSettings());
    }, [dispatch]);

    useEffect(() => {
        if (settings.errorEmails) setEmails(settings.errorEmails);
        if (settings.activityDaysBack) setActivityDays(settings.activityDaysBack);
    }, [settings]);

    const handleAddEmail = () => {
        const trimmed = emailInput.trim();
        if (trimmed && !emails.includes(trimmed)) {
            setEmails([...emails, trimmed]);
            setEmailInput("");
        }
    };

    const handleRemoveEmail = (email: string) => {
        setEmails(emails.filter(e => e !== email));
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddEmail();
        }
    };

    const handleSave = async () => {
        const token = await getApiToken();
        dispatch(updateAdminSettings(emails, activityDays, token));
    };

    return (
        <div className="filter-tool-container">
            <span className="filter-tool-title">{props.getLangString('admin.settingsTitle')}</span>
            <span className="filter-tool-text">{props.getLangString('admin.settingsSubTitle')}</span>
            {loading ?
                <div className="loading-filter-container">
                    <Loader getLangString={props.getLangString} />
                </div>
                :
                error ?
                    <div className="error-tool-container">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="error-tool-icon" />
                        <span className="error-tools-title">{error}</span>
                    </div>
                    :
                    <>
                        <div className="settings-section">
                            <span className="settings-section-title">{props.getLangString('admin.errorEmailsTitle')}</span>
                            <span className="settings-section-text">{props.getLangString('admin.errorEmailsSubTitle')}</span>
                            <div className="settings-email-input-container">
                                <input
                                    type="email"
                                    className="settings-input"
                                    value={emailInput}
                                    onChange={(e) => setEmailInput(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder={props.getLangString('admin.errorEmailsPlaceholder')}
                                />
                                <Button className="settings-add-button" onClick={handleAddEmail}>
                                    {props.getLangString('admin.add')}
                                </Button>
                            </div>
                            <div className="settings-email-list">
                                {emails.map((email, index) => (
                                    <div className="settings-email-chip" key={index}>
                                        <span>{email}</span>
                                        <span className="settings-email-remove" onClick={() => handleRemoveEmail(email)}>&times;</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="settings-divider"></div>

                        <div className="settings-section">
                            <span className="settings-section-title">{props.getLangString('admin.activityDaysTitle')}</span>
                            <span className="settings-section-text">{props.getLangString('admin.activityDaysSubTitle')}</span>
                            <div className="settings-days-input-container">
                                <input
                                    type="number"
                                    className="settings-input settings-input-small"
                                    value={activityDays}
                                    min={1}
                                    onChange={(e) => setActivityDays(Number(e.target.value))}
                                />
                                <span className="settings-days-label">{props.getLangString('admin.days')}</span>
                            </div>
                        </div>

                        <div className="filter-tool-button-container">
                            <Button
                                className="filter-tool-button"
                                onClick={handleSave}
                            >
                                {props.getLangString('admin.save')}
                            </Button>
                        </div>
                    </>
            }
        </div>
    )
}
